import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import Analytics from './components/Analytics';
import SmoothScroll from './components/fx/SmoothScroll';
import CustomCursor from './components/fx/CustomCursor';
import Rail from './components/fx/Rail';
import { AccentProvider } from './components/fx/AccentContext';
import lazyPage from './lib/lazyPage';
import { isCareersPath, isCommunityPath, isLpPath, LP_PATHS } from './lib/routes';
import StaticPage from './components/fx/StaticPage';

// ページは、ページごとに分けた JavaScript で読む（lib/lazyPage.js）
const Home = lazyPage(() => import('./pages/Home'));
const About = lazyPage(() => import('./pages/About'));
const Enablement = lazyPage(() => import('./pages/Enablement'));
const EnablementPlan = lazyPage(() => import('./pages/EnablementPlan'));
const EnablementFaq = lazyPage(() => import('./pages/EnablementFaq'));
const EnablementTopic = lazyPage(() => import('./pages/EnablementTopic'));
const Cases = lazyPage(() => import('./pages/Cases'));
const News = lazyPage(() => import('./pages/News'));
const PodcastEpisode = lazyPage(() => import('./pages/PodcastEpisode'));
const Contact = lazyPage(() => import('./pages/Contact'));
const DocumentRequest = lazyPage(() => import('./pages/DocumentRequest'));
const DocumentsIndex = lazyPage(() => import('./pages/DocumentRequest').then((m) => ({ default: m.DocumentsIndex })));
const Careers = lazyPage(() => import('./pages/Careers'));
const CareersJob = lazyPage(() => import('./pages/CareersJob'));
const Community = lazyPage(() => import('./pages/Community'));
const PrivacyPolicy = lazyPage(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazyPage(() => import('./pages/TermsOfService'));
const TikTokCallback = lazyPage(() => import('./pages/TikTokCallback'));
const NotFound = lazyPage(() => import('./pages/NotFound'));
const LpEnablement = lazyPage(() => import('./pages/LpEnablement'));
const LpOrderEntry = lazyPage(() => import('./pages/LpOrderEntry'));

// ルートの一覧。src/index.js も、最初に開いたページの JavaScript を描く前に読み込むために使う
export const appRoutes = (
  <>
    <Route path="/" element={<Home />} />
    {/* Trooper の製品LPは独立したページ（public/trooper/index.html） */}
    <Route path="/trooper" element={<StaticPage to="/trooper/" />} />
    <Route path="/enablement" element={<Enablement />} />
    <Route path="/enablement/plan" element={<EnablementPlan />} />
    <Route path="/enablement/faq" element={<EnablementFaq />} />
    {/* 検索の言葉ごとのページ（中身は data/enablementTopics.js。prerenderRoutes.js と pageMeta.js にも足す） */}
    <Route path="/enablement/claude-code" element={<EnablementTopic key="claude-code" topicId="claude-code" />} />
    <Route path="/enablement/ai-agent" element={<EnablementTopic key="ai-agent" topicId="ai-agent" />} />
    <Route path="/enablement/non-engineer" element={<EnablementTopic key="non-engineer" topicId="non-engineer" />} />
    {/* 旧プロダクトページ（AI Agent Workforce）は Trooper へ統合 */}
    <Route path="/solutions" element={<StaticPage to="/trooper/" />} />
    <Route path="/solutions/:solutionId" element={<StaticPage to="/trooper/" />} />
    <Route path="/cases" element={<Cases />} />
    <Route path="/cases/:caseId" element={<Cases />} />
    <Route path="/consulting" element={<Navigate to="/cases" replace />} />
    <Route path="/news" element={<News />} />
    <Route path="/news/podcast/:no" element={<PodcastEpisode />} />
    <Route path="/about" element={<About />} />
    <Route path="/about/:memberId" element={<About />} />
    <Route path="/contact" element={<Contact />} />
    <Route path="/documents" element={<DocumentsIndex />} />
    <Route path="/documents/:product" element={<DocumentRequest />} />
    <Route path="/careers" element={<Careers />} />
    <Route path="/careers/:section" element={<Careers />} />
    {/* 募集ごとのページ（Google の求人検索に載せる。一覧の /careers とは別のページ） */}
    <Route path="/careers/jobs/:jobId" element={<CareersJob />} />
    {/* コミュニティ LOCKED IN のLP（別ブランドのため、専用のヘッダーで見せる） */}
    <Route path="/community" element={<Community />} />
    {/* 旧組織ページ（旧グレード別の報酬表）は採用サイトへ統合 */}
    <Route path="/organization" element={<Navigate to="/careers" replace />} />
    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
    <Route path="/terms-of-service" element={<TermsOfService />} />
    <Route path="/tiktok/callback" element={<TikTokCallback />} />
    {/* 検索広告の着地ページ（専用のヘッダー・フッターで見せ、検索には出さない。lib/routes.js の LP_PATHS） */}
    <Route path={LP_PATHS.enablement} element={<LpEnablement />} />
    <Route path={LP_PATHS.orderEntry} element={<LpOrderEntry />} />
    {/* 存在しない URL（検索に載せない。本番では CloudFront が build/404.html を 404 で返す） */}
    <Route path="*" element={<NotFound />} />
  </>
);

const AppContent = () => {
  const location = useLocation();
  const lp = isLpPath(location.pathname);
  const hideCorporateChrome =
    location.pathname === '/tiktok/callback' ||
    isCareersPath(location.pathname) ||
    isCommunityPath(location.pathname) ||
    lp;

  return (
    <div className="App min-h-screen bg-paper text-ink">
      {!hideCorporateChrome && <Header />}
      {!hideCorporateChrome && <Rail />}
      {/* 開いたページの JavaScript を読み込んでいる間は、何も出さない（画面内の移動では前のページを出したまま待つ） */}
      <Suspense fallback={null}>
        <Routes>{appRoutes}</Routes>
      </Suspense>
      {!hideCorporateChrome && <Footer />}
      {/* カスタムカーソルと粒子はサイトの装飾。広告の着地ページでは出さない */}
      {!lp && <CustomCursor />}
      {!lp && <div className="fx-grain" aria-hidden="true" />}
    </div>
  );
};

const App = () => {
  return (
    // v7_startTransition: 画面内の移動では、次のページの JavaScript が届くまで前のページを出したままにする
    <BrowserRouter future={{ v7_startTransition: true }}>
      <ScrollToTop />
      {/* Google アナリティクス（測定 ID が無いビルドでは何もしない。lib/analytics.js） */}
      <Analytics />
      <SmoothScroll />
      <AccentProvider>
        <AppContent />
      </AccentProvider>
    </BrowserRouter>
  );
};

export default App;
